/**
 * 캔버스에 이미 있는 노드를 끌어 **그룹(서브플로·반복그룹)에 넣거나 꺼내기** — 끌기를 놓았을 때 새 소속을 정한다.
 *
 * 규칙
 *   · 넣기: 놓은 노드의 **가운데**가 어떤 그룹 안에 있으면 그 그룹의 노드가 된다. 그룹이 겹쳐 있으면 가장 안쪽(깊은) 그룹.
 *   · 꺼내기: 그룹 안 노드의 가운데가 **끌기 시작할 때의 그 그룹 경계** 밖으로 나가면 그룹에서 빠진다
 *     (그 자리를 덮는 다른 그룹이 있으면 그 그룹으로, 없으면 최상위로).
 *     경계에 조금 걸치는 정도면 그대로 — 자동 크기 그룹은 자식을 바깥쪽으로 끌면 커지는 기능이 있다.
 *   · 대상에서 빼는 그룹: 자기 자신과 자기 안의 그룹(순환), 읽기 전용(자동 생성) 그룹.
 * 화면 위치는 그대로 두고 좌표만 새 부모 기준으로 바꾼다.
 */
import type { Node } from '@xyflow/react';
import { isGroupNodeType } from '../utils';

export interface Rect { x: number; y: number; w: number; h: number }

export interface MembershipChange { parentId: string | null; position: { x: number; y: number } }

const inside = (r: Rect, x: number, y: number) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;

function depthOf(id: string, byId: Map<string, Node>): number {
  let d = 0;
  let p = byId.get(id)?.parentId;
  while (p) { d++; p = byId.get(p)?.parentId; }
  return d;
}

export function descendantsOf(id: string, nodes: Node[]): Set<string> {
  const out = new Set<string>();
  let grew = true;
  while (grew) {
    grew = false;
    nodes.forEach((n) => {
      if (n.parentId && (n.parentId === id || out.has(n.parentId)) && !out.has(n.id)) { out.add(n.id); grew = true; }
    });
  }
  return out;
}

/** 이 점을 덮는 가장 안쪽 그룹 — exclude 에 든 그룹은 빼고 */
export function groupAt(x: number, y: number, nodes: Node[], absRect: (id: string) => Rect | null, exclude: Set<string>): string | null {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  let best: { id: string; depth: number; area: number } | null = null;
  nodes.forEach((g) => {
    if (!isGroupNodeType(g.type || '') || exclude.has(g.id) || (g.data as any)?.readOnly) return;
    const r = absRect(g.id);
    if (!r || !inside(r, x, y)) return;
    const depth = depthOf(g.id, byId);
    const area = r.w * r.h;
    if (!best || depth > best.depth || (depth === best.depth && area < best.area)) best = { id: g.id, depth, area };
  });
  return best ? (best as { id: string }).id : null;
}

/**
 * 놓은 노드들의 새 소속. 바뀌는 것만 돌려준다.
 * @param draggedIds 끌어 놓은 노드들 (여럿이면 그중 다른 끌린 노드의 자식인 것은 부모를 따라가므로 뺀다)
 * @param startParentRect 끌기 시작할 때 각 노드의 부모 그룹 경계(절대 좌표)
 */
export function planMembership(
  draggedIds: string[], nodes: Node[], absRect: (id: string) => Rect | null, startParentRect: Map<string, Rect>,
): Map<string, MembershipChange> {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const dragged = new Set(draggedIds);
  const out = new Map<string, MembershipChange>();
  draggedIds.forEach((id) => {
    const n = byId.get(id);
    if (!n) return;
    for (let p = n.parentId; p; p = byId.get(p)?.parentId) if (dragged.has(p)) return;   // 부모를 따라 움직인다
    const r = absRect(id);
    if (!r) return;
    const cx = r.x + r.w / 2;
    const cy = r.y + r.h / 2;
    const cur = n.parentId || null;
    const start = cur ? startParentRect.get(id) : undefined;
    const leaving = !!(cur && start && !inside(start, cx, cy));
    // 꺼낼 때는 지금 그룹을 후보에서 뺀다 — 자동 크기 그룹은 끄는 동안 노드를 따라 커져서, 놓는 순간에도 노드를 덮고 있다
    const exclude = new Set([id, ...Array.from(descendantsOf(id, nodes)), ...(leaving && cur ? [cur] : [])]);
    const target = groupAt(cx, cy, nodes, absRect, exclude);
    // 지금 그룹의 (시작 때) 경계 안이면 그대로 두되, 그 안의 더 깊은 그룹으로는 들어갈 수 있다
    const next = cur && !leaving ? (target ?? cur) : target;
    if (next === cur) return;
    const pr = next ? absRect(next) : null;
    out.set(id, { parentId: next, position: { x: Math.round(r.x - (pr?.x ?? 0)), y: Math.round(r.y - (pr?.y ?? 0)) } });
  });
  return out;
}

/** React Flow 는 부모가 자식보다 **앞에** 있어야 한다 — 깊이 순으로 안정 정렬 */
export function parentsFirst<N extends Node>(nodes: N[]): N[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return nodes.map((n, i) => ({ n, i, d: depthOf(n.id, byId as any) })).sort((a, b) => a.d - b.d || a.i - b.i).map((x) => x.n);
}
